"use client";

/**
 * The collection page — hero, how it divides, what it is for, where it stops, what a record holds.
 *
 * ONE PAGE FOR BOTH KINDS OF COLLECTION. There used to be two renderers: CorpusPage for a
 * corpus-backed collection with facets and a shelf split, and SimpleCollectionPage for a
 * storage-backed one with a count and little else. They were two different products to look at,
 * and the difference a reader could see had nothing to do with the difference that actually
 * exists. This is one page whose middle section either divides or says plainly that it does not —
 * which is a fact about the collection, and reads as one.
 *
 * NOTHING HERE IS PLACEHOLDER-SHAPED. Every block is dropped when its figure is missing rather
 * than rendered with a dash, a zero or a skeleton. These pages exist to make a claim about what
 * the index holds; a page showing "—" where its central number goes looks broken, and one showing
 * "0" is asserting something false. The arithmetic and that rule both live in lib/collection-view.
 *
 * THE BANDS ARE INK, NOT COLOUR. askFinz is a monochrome system and colour is reserved for
 * meaning, so the ramp is the foreground mixed into the background at falling strength and the
 * accent appears on exactly one band: the one the reader has selected. That way the only coloured
 * thing on the page is the thing they just pointed at.
 */

import { useState } from "react";
import Link from "next/link";
import { ArrowUpRight } from "lucide-react";
import type { Cadence, Dim, FieldRow, RailCell } from "@/lib/collection-view";
import { readoutFor } from "@/lib/collection-view";

export type CollectionDetailProps = {
  /* The registry's reviewed category label. NOT the corpus key: for a storage-backed
     collection that key is the internal store name, which must never reach the markup. */
  eyebrow: string;
  title: string;
  lede: string;
  unit: string;
  /** The unit the BANDS are counted in, when it differs from the headline's.
   *  The web index is the one collection where it does: it is measured in pages, but a facet runs
   *  over vector records, so the breakdown counts passages. Saying "13,163,119 pages are filed
   *  under General" on a shelf whose headline reads 3.3M pages is a plain contradiction; saying
   *  passages is simply what was counted. Defaults to `unit`. */
  dimUnit?: string;
  countShort: string | null;
  deltaPct: number | null;
  growth: { line: string; area: string; samples: number } | null;
  rail: RailCell[];
  dims: Dim[];
  /** Said instead of the bands when the collection genuinely has no dimension to draw. */
  emptyNote: string;
  capabilities: string[];
  questions: string[];
  cadence: Cadence | null;
  caveats: string[];
  fields: FieldRow[];
  recordNote?: string;
  collectionTotal: number | null;
  relatedApp: { slug: string; name: string; note: string; href?: string };
};

/* The ink ramp. Falling strength so the first band reads as the largest before any number is read;
   past the sixth the difference stops being legible, so it holds. */
const RAMP = [88, 68, 52, 40, 30, 22, 16];
const bandInk = (step: number) => RAMP[Math.min(step, RAMP.length - 1)];

export function CollectionDetail(p: CollectionDetailProps) {
  /* One selection per dimension, plus which dimension was touched last — the readout speaks for
     that one, so pressing a band in the second chart does not leave the sentence describing the
     first. Defaults to nothing selected, and the readout then speaks for the largest band. */
  const [sel, setSel] = useState<Record<string, number>>({});
  const [lastDim, setLastDim] = useState<string | null>(null);

  const active = p.dims.find((d) => d.id === (lastDim ?? p.dims[0]?.id)) ?? p.dims[0];
  const readout = active ? readoutFor(active, sel[active.id] ?? 0, p.dimUnit ?? p.unit) : null;

  return (
    <div className="cd">
      {/* ── Hero ─────────────────────────────────────────────────────────────────────────── */}
      <div className="cd-hero">
        <div className="cd-hero-main">
          <div className="cd-tag">{p.eyebrow}</div>
          <h1 className="cd-h1">{p.title}</h1>
          <p className="cd-lede">{p.lede}</p>

          {p.countShort && (
            <div className="cd-count">
              <span className="cd-count-n">{p.countShort}</span>
              <span className="cd-count-u">{p.unit}</span>
            </div>
          )}

          <div className="cd-hero-links">
            <a
              className="cd-link-strong"
              href={`https://${p.relatedApp.slug}.askfinz.ai`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Search this collection <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </a>
            {p.fields.length > 0 && (
              <a className="cd-link-quiet" href="#field-reference">
                Field reference
              </a>
            )}
          </div>
        </div>

        {/* Growth. Rendered only from a real series — see growthPaths. */}
        {p.growth && (
          <aside className="cd-growth" aria-label="How this collection has grown while it has been tracked">
            <div className="cd-growth-head">
              <span className="cd-micro">growth · last 24h</span>
              {p.deltaPct != null && <span className="cd-growth-delta">+{p.deltaPct}%</span>}
            </div>
            <svg
              viewBox="0 0 360 130"
              width="100%"
              height="130"
              preserveAspectRatio="none"
              className="cd-growth-svg"
              role="img"
              aria-label="Growth over the tracked window. A shape rather than a chart — no axis, because the page publishes shares and not the totals behind them."
            >
              <path d={p.growth.area} fill="currentColor" opacity="0.07" />
              <path
                d={p.growth.line}
                fill="none"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
                opacity="0.75"
              />
            </svg>
            <div className="cd-growth-foot">
              <span>t−24h</span>
              <span>{p.growth.samples} samples</span>
              <span>now</span>
            </div>
          </aside>
        )}
      </div>

      {/* ── The rail ─────────────────────────────────────────────────────────────────────── */}
      {/* A THIN RAIL DOES NOT STRETCH TO FILL THE BOX.
          Cells are one per dimension plus fields-per-record and per-record, so a collection that
          divides four ways gets six and a flat one gets two — and `1fr` each meant those two grew to
          345px apiece while the six-cell version sat at 230px. `/collections/web-pages` was the worst
          of it: a single cell spanning the full width, which reads as a broken table rather than one
          fact. Below three cells they keep a natural width and an empty track takes up the slack, so
          the rail looks the same on every page and only its length changes. */}
      {p.rail.length > 0 && (
        <dl
          className="cd-rail"
          style={{
            gridTemplateColumns:
              p.rail.length < 3
                ? `repeat(${p.rail.length}, minmax(0, 260px)) 1fr`
                : `repeat(${p.rail.length}, minmax(0,1fr))`,
          }}
        >
          {p.rail.map((r) => (
            <div className="cd-rail-cell" key={r.k}>
              <dt className="cd-micro">{r.k}</dt>
              <dd className="cd-rail-v">{r.v}</dd>
            </div>
          ))}
        </dl>
      )}

      {/* ── How it divides ───────────────────────────────────────────────────────────────── */}
      {p.dims.length > 0 ? (
        <section className="cd-divides" aria-labelledby="cd-divides-h">
          <div className="cd-sec-head">
            <h2 className="cd-sec-h" id="cd-divides-h">How it divides</h2>
            <span className="cd-micro">
              {p.dims.length === 1 ? "1 dimension" : `${p.dims.length} dimensions`} · press a band to read it
            </span>
          </div>

          {p.dims.map((d) => (
            <div className="cd-dim" key={d.id}>
              <div className="cd-dim-head">
                <span className="cd-dim-eyebrow">{d.eyebrow}</span>
                <span className="cd-dim-declared">{d.declaredMono}</span>
              </div>

              <div className="cd-bands">
                {d.segs.map((sg, i) => {
                  const on = (sel[d.id] ?? -1) === i;
                  const ink = bandInk(sg.step);
                  return (
                    <button
                      type="button"
                      key={sg.label}
                      className={"cd-band" + (on ? " cd-band-on" : "")}
                      style={{
                        width: sg.width,
                        background: on
                          ? "hsl(var(--accent))"
                          : `color-mix(in oklab, hsl(var(--foreground)) ${ink}%, hsl(var(--background)))`,
                        /* White, not a token: the accent is red in BOTH themes, so paper ink would
                           be near-black on red in dark mode. The design specifies white here. */
                        color: on
                          ? "#ffffff"
                          : ink >= 45
                            ? "hsl(var(--background))"
                            : "hsl(var(--foreground))",
                      }}
                      onClick={() => {
                        setSel((s) => ({ ...s, [d.id]: i }));
                        setLastDim(d.id);
                      }}
                      aria-pressed={on}
                      aria-label={`${sg.label}, ${sg.countFmt} ${p.unit}, ${sg.pct}`}
                    >
                      {sg.shortLabel && <span className="cd-band-label">{sg.shortLabel}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Every value, named. The bands drop a label they cannot fit; this never does. */}
              <ul className="cd-legend">
                {d.segs.map((sg) => (
                  <li className="cd-legend-item" key={sg.label}>
                    <span
                      className="cd-legend-swatch"
                      aria-hidden
                      style={{
                        background: `color-mix(in oklab, hsl(var(--foreground)) ${bandInk(sg.step)}%, hsl(var(--background)))`,
                      }}
                    />
                    <span className="cd-legend-label">{sg.label}</span>
                    <span className="cd-legend-n">{sg.countFmt} · {sg.pct}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          {readout && (
            <div className="cd-readout" aria-live="polite">
              <div className="cd-micro">readout</div>
              <p className="cd-readout-line">{readout.readout}</p>
              <p className="cd-readout-note">{readout.note}</p>
            </div>
          )}
        </section>
      ) : (
        <section className="cd-nodims" aria-labelledby="cd-nodims-h">
          <div className="cd-sec-head cd-sec-head-strong">
            <h2 className="cd-sec-h" id="cd-nodims-h">How it divides</h2>
            <span className="cd-micro">no dimension</span>
          </div>
          {/* IN A CARD, like every other page's breakdown.
              This section used to be the only one on the page that was bare text — no panel, no
              border — beside pages whose "How it divides" ends in a bordered readout. A reader
              moving between two collections could not tell whether this one was built differently
              or had simply failed to load. The words differ because the data does; the furniture
              should not. */}
          <div className="cd-readout cd-nodims-card">
            <p className="cd-nodims-line">It doesn&rsquo;t. One shape, filed flat.</p>
            <p className="cd-readout-note">{p.emptyNote}</p>
          </div>
        </section>
      )}

      {/* ── What it is for, and what writes to it ────────────────────────────────────────── */}
      <section className="cd-cols">
        {p.capabilities.length > 0 && (
          <div className="cd-col">
            <h2 className="cd-col-h">What you can do with it</h2>
            {p.capabilities.map((cap) => (
              <p className="cd-col-item" key={cap}>{cap}</p>
            ))}
          </div>
        )}

        {p.questions.length > 0 && (
          <div className="cd-col">
            <h2 className="cd-col-h">Filed to answer</h2>
            {p.questions.map((q) => (
              <p className="cd-col-item cd-col-item-quiet" key={q}>{q}</p>
            ))}
          </div>
        )}

        <div className="cd-col cd-col-side">
          {p.cadence && (
            <div className="cd-side-block">
              <h2 className="cd-col-h">Written to</h2>
              <dl className="cd-kv">
                {p.cadence.every && (
                  <div className="cd-kv-row">
                    <dt className="cd-micro">cadence</dt>
                    <dd className="cd-kv-v">{p.cadence.every}</dd>
                  </div>
                )}
                {p.cadence.last && (
                  <div className="cd-kv-row">
                    <dt className="cd-micro">last write</dt>
                    <dd className="cd-kv-v cd-kv-mono">{p.cadence.last}</dd>
                  </div>
                )}
                {p.cadence.added && (
                  <div className="cd-kv-row">
                    <dt className="cd-micro">added</dt>
                    <dd className="cd-kv-v cd-kv-mono">{p.cadence.added}</dd>
                  </div>
                )}
              </dl>
              <p className="cd-side-note">{p.cadence.what}</p>
            </div>
          )}

          <div className="cd-side-block cd-side-block-top">
            <h2 className="cd-col-h cd-col-h-quiet">Read by</h2>
            <Link href={p.relatedApp.href ?? `/apps/${p.relatedApp.slug}`} className="cd-app-link">
              {p.relatedApp.name} <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
            </Link>
            <div className="cd-micro">{p.relatedApp.slug}.askfinz.ai</div>
          </div>
        </div>
      </section>

      {/* ── Where it stops ──────────────────────────────────────────────────────────────────
          Part of the page rather than an omission. A collection page that lists only what it holds
          is a brochure; the reader deciding whether to rely on it needs the edges more than the
          middle. ── */}
      {p.caveats.length > 0 && (
        <section className="cd-stops">
          <h2 className="cd-col-h">Where it stops</h2>
          <div className="cd-stops-grid">
            {p.caveats.map((cv) => (
              <p className="cd-stop" key={cv}>{cv}</p>
            ))}
          </div>
        </section>
      )}

      {/* ── Field reference ─────────────────────────────────────────────────────────────────
          The columns beyond the name appear only where the collection publishes them. Nothing here
          is derived from a field's name: a type or a coverage figure invented from "decision_date"
          would look exactly as authoritative as a real one. ── */}
      {p.fields.length > 0 && (
        <section className="cd-fields" id="field-reference">
          <div className="cd-sec-head">
            <h2 className="cd-col-h">Field reference</h2>
            <span className="cd-micro">
              {p.fields.length} {p.fields.length === 1 ? "field" : "fields"} per record
            </span>
          </div>
          {p.recordNote && <p className="cd-fields-note">{p.recordNote}</p>}

          <FieldTable fields={p.fields} total={p.collectionTotal} />
        </section>
      )}
    </div>
  );
}

/**
 * The record schema.
 *
 * Two shapes, chosen by what the collection can actually say. Where presence figures exist the
 * table earns its columns; where only names do, a column of empty cells beside a column of dashes
 * would be a table pretending to hold data, so the names are set as a list instead.
 */
function FieldTable({ fields, total }: { fields: FieldRow[]; total: number | null }) {
  const hasPresence = total != null && total > 0 && fields.some((f) => typeof f.present === "number");
  const hasType = fields.some((f) => f.type);

  if (!hasPresence && !hasType) {
    return (
      <ul className="cd-fields-plain">
        {fields.map((f) => (
          <li className="cd-field-plain" key={f.name}>
            <span className="cd-field-name">{f.name}</span>
            {f.note && <span className="cd-field-note">{f.note}</span>}
          </li>
        ))}
      </ul>
    );
  }

  return (
    <div className={"cd-table" + (hasPresence ? "" : " cd-table-2")}>
      <div className="cd-tr cd-th">
        <span className="cd-micro">field</span>
        {hasType && <span className="cd-micro">type</span>}
        {hasPresence && <span className="cd-micro">present on</span>}
      </div>
      {fields.map((f) => {
        const share = hasPresence && typeof f.present === "number" && total
          ? (f.present / total) * 100
          : null;
        return (
          <div className="cd-tr" key={f.name}>
            <span className="cd-field-name">{f.name}</span>
            {hasType && <span className="cd-field-type">{f.type ?? ""}</span>}
            {hasPresence && (
              <span className="cd-field-present">
                {share != null ? (
                  <>
                    <span className="cd-field-present-n">
                      {f.present!.toLocaleString("en-US")}
                      {share >= 99.95 ? " · all" : ` · ${share.toFixed(1)}%`}
                    </span>
                    <span className="cd-field-bar" aria-hidden>
                      <span className="cd-field-bar-fill" style={{ width: `${share.toFixed(1)}%` }} />
                    </span>
                  </>
                ) : null}
              </span>
            )}
            {f.note && <span className="cd-field-note">{f.note}</span>}
          </div>
        );
      })}
    </div>
  );
}

export default CollectionDetail;
